import dayjs from 'dayjs';
import { Space } from 'antd';
import {
    FC,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/PageHeader';
import { useRequestsContext } from '../../components/Requests/context/requests.ctx';
import { Filters, TableRequests } from '../../components/Requests/components';
import { TableActions } from '../../components/TableActions';
import { requestersSocket } from '../../sockets/modules/requests-socket.module';
import { useWorkspaceStore } from '../../store/workspace.store';
import { IRequest } from '../../interfaces/requests.interface';
import { exportTableData } from '../../utils/export.util';
import { getStatusText } from '../../components/Requests/utils/requests.util';

const REQUEST_COLUMNS = ['createdAt', 'bookingNumericId', 'businessName', 'customerName', 'status'];

const RequestsScreen: FC = () => {
    const { t } = useTranslation('small-fleet-orders');
    const { t: tb } = useTranslation('bookings', { keyPrefix: 'TABLE' });

    const workspace = useWorkspaceStore((state) => state.data);

    const [columnsSelected, setColumnsSelected] = useState<string[]>(REQUEST_COLUMNS);

    const {
        data,
        pagination,
        searchParams,
        handleSearchParam,
        fetchData,
    } = useRequestsContext();

    useEffect(() => {
        fetchData();
    }, [searchParams, pagination]);

    useEffect(() => {
        requestersSocket.send(workspace?.keyName || '');
    }, [workspace?.keyName]);

    const columnLabels: Record<string, string> = useMemo(() => ({
        createdAt: tb('DATE'),
        bookingNumericId: tb('SERVICE_ID'),
        businessName: tb('STORE'),
        customerName: tb('CUSTOMER'),
        status: tb('STATUS'),
    }), [tb]);

    const handleExport = () => {
        exportTableData<IRequest>(
            data,
            [
                { key: 'bookingNumericId', label: tb('SERVICE_ID') },
                { key: 'createdAt', label: tb('DATE'), render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm') },
                { key: 'businessName', label: tb('STORE') },
                { key: 'customerName', label: tb('CUSTOMER') },
                {
                    key: 'status',
                    label: tb('STATUS'),
                    render: (v, record) => {
                        const statusText = getStatusText(v, workspace?.keyName as string, record.providerName as string);
                        return t(statusText);
                    },
                },
            ],
            `solicitudes-${dayjs().format('YYYY-MM-DD')}`,
        );
    };

    return (
        <div className="screen">
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <PageHeader
                    title={t('HEADER')}
                    style={{ padding: 0 }}
                />
                <Filters
                    loading={false}
                    refreshData={fetchData}
                    handleSearchParam={handleSearchParam}
                    extra={(
                        <TableActions
                            columnsSelected={columnsSelected}
                            availableColumns={REQUEST_COLUMNS}
                            columnLabels={columnLabels}
                            onColumnsChange={setColumnsSelected}
                            onExport={handleExport}
                            onReload={fetchData}
                            exportDisabled={!data?.length}
                        />
                    )}
                />
                <TableRequests columnsSelected={columnsSelected} />
            </Space>
        </div>
    );
};

export { RequestsScreen };
